import { datadogRum } from '@datadog/browser-rum'

/**
 * Safe fetch wrapper:
 * - Reports only when response is non-2xx OR fetch throws
 * - Redacts common secrets from URL/query + body
 * - Truncates response body to a small limit
 * - Reports errors asynchronously without blocking the original response
 */

type Config = {
  maxBodyChars: number
  captureBody: boolean
  ignoredOrigins: string[]
  redactKeys: string[]
}

const DEFAULTS: Config = {
  maxBodyChars: 2000,
  captureBody: true,
  ignoredOrigins: [
    'datadoghq.eu', // DD RUM intake
    'datadoghq.com',
    'launchdarkly.com', // feature flags
    'segment.io', // analytics
    'segment.com',
    'usepylon.com', // support widget
    'getbeamer.com', // product updates
    'courier.com', // notifications
    'flagcdn.com', // flag assets
  ],
  redactKeys: [
    'token', // access_token, refresh_token, id_token, cubeToken, courierToken, oneSchemaToken, etc.
    'auth', // authorization header, authChallenge (magic link)
    'password',
    'secret', // client_secret, api_secret, AUTH_SECRET, etc.
    'api_key',
    'apikey',
    'credential',
    'session', // session cookies/identifiers
    'cookie', // cookie + set-cookie headers
    'fingerprint', // omnea_fingerprint_* security cookies
  ],
}

const escapeRegExp = (str: string): string => String(str).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const truncate = (s: string, max: number): string => {
  if (s.length <= max) return s
  return s.slice(0, max) + '…'
}

const shouldRedactKey = (k: string, redactKeys: string[]): boolean => {
  const key = String(k).toLowerCase()
  return redactKeys.some((rk) => key === rk || key.endsWith(rk) || key.includes(rk))
}

const redactSecrets = (text: string, redactKeys: string[]): string => {
  if (!text) return text

  let out = text

  for (const k of redactKeys) {
    const key = escapeRegExp(k)
    const jsonRe = new RegExp(`("[^"]*${key}[^"]*"\\s*:\\s*)"(.*?)"`, 'gi')
    out = out.replace(jsonRe, '$1"[REDACTED]"')

    const kvRe = new RegExp(`(${key}\\s*[=:]\\s*)([^\\s&",}]+)`, 'gi')
    out = out.replace(kvRe, '$1[REDACTED]')
  }

  out = out.replace(/(Authorization\s*[:=]\s*Bearer\s+)([A-Za-z0-9._-]+)/gi, '$1[REDACTED]')
  out = out.replace(/(Bearer\s+)([A-Za-z0-9._-]+)/gi, '$1[REDACTED]')

  return out
}

const sanitizeUrl = (url: string, redactKeys: string[]): string => {
  try {
    const u = new URL(url, window.location.href)
    const keys = Array.from(u.searchParams.keys())
    for (const key of keys) {
      if (shouldRedactKey(key, redactKeys)) {
        u.searchParams.set(key, '[REDACTED]')
      }
    }
    return u.toString()
  } catch {
    return redactSecrets(String(url), redactKeys)
  }
}

const isIgnoredOrigin = (url: string, ignoredOrigins: string[]): boolean => {
  try {
    const hostname = new URL(url, window.location.href).hostname
    return ignoredOrigins.some((origin) => hostname === origin || hostname.endsWith(`.${origin}`))
  } catch {
    return false
  }
}

const normalizeRequest = (input: RequestInfo | URL, init?: RequestInit): { url: string; method: string } => {
  const method = (init?.method ?? (input instanceof Request ? input.method : null) ?? 'GET').toUpperCase()

  const url = typeof input === 'string' ? input : input instanceof Request ? input.url : String(input)

  return { url, method }
}

type ErrorContext = {
  kind: string
  url: string
  method: string
  status: number
  statusText: string
  durationMs: number
  requestBody?: string
  requestBodyTruncated?: boolean
  responseBody?: string
  responseBodyTruncated?: boolean
  responseContentType?: string
}

const extractRequestBody = (input: RequestInfo | URL, init?: RequestInit): BodyInit | null | undefined =>
  init?.body ?? (input instanceof Request ? input.body : null)

const safeReadRequestBody = (
  cfg: Config,
  input: RequestInfo | URL,
  init?: RequestInit
): { requestBody?: string; requestBodyTruncated?: boolean } => {
  try {
    const body = extractRequestBody(input, init)
    if (body == null) return {}

    const text = typeof body === 'string' ? body : body instanceof URLSearchParams ? body.toString() : null
    if (text == null) return {}

    const redacted = redactSecrets(text, cfg.redactKeys)
    return {
      requestBody: truncate(redacted, cfg.maxBodyChars),
      requestBodyTruncated: redacted.length > cfg.maxBodyChars,
    }
  } catch {
    return {}
  }
}

const isTextContentType = (ct: string): boolean =>
  ct.includes('application/json') ||
  ct.includes('text/') ||
  ct.includes('application/problem+json') ||
  ct.includes('application/xml') ||
  ct.includes('application/x-www-form-urlencoded')

const buildSafeErrorContext = async (
  cfg: Config,
  {
    url,
    method,
    status,
    statusText,
    durationMs,
    response,
    input,
    init,
  }: {
    url: string
    method: string
    status: number
    statusText: string
    durationMs: number
    response: Response
    input: RequestInfo | URL
    init?: RequestInit
  }
): Promise<ErrorContext> => {
  const safeUrl = sanitizeUrl(url, cfg.redactKeys)

  const context: ErrorContext = {
    kind: 'http_error',
    url: safeUrl,
    method,
    status,
    statusText,
    durationMs,
    ...safeReadRequestBody(cfg, input, init),
  }

  if (!cfg.captureBody) return context

  try {
    const ct = response.headers?.get?.('content-type') ?? ''
    if (!isTextContentType(ct)) return context

    const text = await response.text()
    const redacted = redactSecrets(text, cfg.redactKeys)

    context.responseBody = truncate(redacted, cfg.maxBodyChars)
    context.responseBodyTruncated = redacted.length > cfg.maxBodyChars
    context.responseContentType = ct
  } catch {
    // Swallow body-read/CORS errors; keep context minimal
  }

  return context
}

let installed = false

export const installDDFetchErrorCapture = (options: Partial<Config> = {}) => {
  if (typeof window === 'undefined') return
  if (installed) return
  installed = true

  const cfg: Config = { ...DEFAULTS, ...options }
  const originalFetch = window.fetch.bind(window)

  window.fetch = async function safeFetch(input, init) {
    const startedAt = Date.now()
    const { url, method } = normalizeRequest(input, init)

    const ignored = isIgnoredOrigin(url, cfg.ignoredOrigins)

    try {
      const res = await originalFetch(input, init)

      if (!res.ok && !ignored) {
        const resClone = res.clone()
        void (async () => {
          try {
            const context = await buildSafeErrorContext(cfg, {
              url,
              method,
              status: res.status,
              statusText: res.statusText,
              durationMs: Date.now() - startedAt,
              response: resClone,
              input,
              init,
            })

            datadogRum.addError(new Error(`HTTP ${res.status} ${method} ${context.url}`), context)
          } catch {
            // Swallow errors from observability to avoid impacting callers
          }
        })()
      }

      return res
    } catch (err: unknown) {
      const isAbortError = err instanceof DOMException && err.name === 'AbortError'
      if (!ignored && !isAbortError) {
        const error = err instanceof Error ? err : new Error(String(err))
        const context = {
          kind: 'network_error',
          url: sanitizeUrl(url, cfg.redactKeys),
          method,
          durationMs: Date.now() - startedAt,
          ...safeReadRequestBody(cfg, input, init),
        }

        datadogRum.addError(error, context)
      }

      throw err
    }
  }
}
